import { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Button } from '@/core/components/ui/button';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { Progress } from '@/core/components/ui/progress';
import { Upload, X, AlertCircle, FileCode, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { formatFileSize } from '../../types/sound-management-types';

const MAX_JS_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function isValidJsFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.js');
}

interface FileWithStatus {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface UploadWeezmoDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  error: string | null;
}

export function UploadWeezmoDialog({ open, onClose, onUpload, isUploading, error }: UploadWeezmoDialogProps) {
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localUploading, setLocalUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = useCallback(() => {
    setFiles([]);
    setIsDragging(false);
    setIsProcessing(false);
    setUploadProgress(0);
    setLocalUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (!localUploading && !isProcessing) {
      resetForm();
      onClose();
    }
  }, [localUploading, isProcessing, resetForm, onClose]);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const validFiles: FileWithStatus[] = [];
    for (const file of Array.from(incoming)) {
      if (!isValidJsFile(file)) continue;
      if (file.size > MAX_JS_FILE_SIZE) continue;
      validFiles.push({
        id: `${file.name}-${file.size}`,
        file,
        status: 'pending',
      });
    }
    setFiles((prev) => {
      const existingIds = new Set(prev.map((f) => f.id));
      return [...prev, ...validFiles.filter((f) => !existingIds.has(f.id))];
    });
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleRemoveFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleUpload = useCallback(async () => {
    const pending = files.filter((f) => f.status === 'pending');
    if (pending.length === 0) return;

    setLocalUploading(true);
    setUploadProgress(0);

    let completed = 0;
    for (const fileItem of pending) {
      setFiles((prev) => prev.map((f) => f.id === fileItem.id ? { ...f, status: 'uploading' } : f));
      try {
        await onUpload(fileItem.file);
        setFiles((prev) => prev.map((f) => f.id === fileItem.id ? { ...f, status: 'success' } : f));
      } catch (err) {
        setFiles((prev) => prev.map((f) =>
          f.id === fileItem.id
            ? { ...f, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' }
            : f
        ));
      }
      completed++;
      setUploadProgress(Math.round((completed / pending.length) * 100));
    }

    setLocalUploading(false);
  }, [files, onUpload]);

  const pendingFiles = files.filter((f) => f.status === 'pending');
  const allComplete = files.length > 0 && files.every((f) => f.status === 'success' || f.status === 'error');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[560px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Upload Weezmo JS Files</DialogTitle>
          <DialogDescription>
            Upload .js files for Weezmo Web. Only .js files up to {formatFileSize(MAX_JS_FILE_SIZE)} are accepted.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Drop Zone */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".js,application/javascript"
            onChange={handleFileChange}
            className="hidden"
            multiple
          />
          <div
            onClick={isProcessing ? undefined : () => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
              isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'
            )}
          >
            <Upload className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Drop .js files here or <span className="text-primary font-medium">browse</span>
            </p>
            <p className="mt-1 text-xs text-gray-500">
              .js files only · Max {formatFileSize(MAX_JS_FILE_SIZE)} per file
            </p>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="border rounded-lg divide-y max-h-[280px] overflow-y-auto">
              {files.map((fileItem) => (
                <div key={fileItem.id} className="p-3 flex items-center gap-3">
                  <div className="flex-shrink-0">
                    {fileItem.status === 'pending' && <FileCode className="h-5 w-5 text-gray-400" />}
                    {fileItem.status === 'uploading' && <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />}
                    {fileItem.status === 'success' && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                    {fileItem.status === 'error' && <XCircle className="h-5 w-5 text-red-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium font-mono truncate">{fileItem.file.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(fileItem.file.size)}
                      {fileItem.error && <span className="text-red-500 ml-2">{fileItem.error}</span>}
                    </p>
                  </div>
                  {fileItem.status === 'pending' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(fileItem.id)}
                      className="h-8 w-8 p-0 flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* API error */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Progress */}
          {localUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={localUploading || isUploading}>
            {allComplete ? 'Close' : 'Cancel'}
          </Button>
          {!allComplete && (
            <Button
              onClick={handleUpload}
              disabled={pendingFiles.length === 0 || localUploading || isUploading}
            >
              {localUploading || isUploading
                ? 'Uploading...'
                : `Upload ${pendingFiles.length} File${pendingFiles.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
